import { CircleAlert, CircleCheck, CircleDashed, Clock, Info } from "lucide-react";
import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

/** design/04 §0 "status is colour AND word": green on sale, grey draft, red problem. */
export type StatusTone = "active" | "draft" | "problem" | "pending" | "info";

const TONE: Readonly<
  Record<
    StatusTone,
    { className: string; Icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }> }
  >
> = {
  // dark text on light fills: every pair ≥ 4.5:1 (AA)
  active: { className: "border-[#047857] bg-[#ecfdf5] text-[#065f46]", Icon: CircleCheck },
  draft: { className: "border-[#6b6b6b] bg-[#f2f2f2] text-[#333333]", Icon: CircleDashed },
  problem: { className: "border-[#b91c1c] bg-[#fef2f2] text-[#991b1b]", Icon: CircleAlert },
  pending: { className: "border-[#b45309] bg-[#fffbeb] text-[#92400e]", Icon: Clock },
  info: { className: "border-[#1d4ed8] bg-[#eff6ff] text-[#1e40af]", Icon: Info },
};

export interface StatusChipProps {
  tone: StatusTone;
  /** The word shown (defaults to the standard word of the tone). */
  label?: string;
  /** For "problem": what the button opens (the fix instructions). */
  onClick?: () => void;
  className?: string;
}

export function StatusChip({ tone, label, onClick, className }: StatusChipProps) {
  const { t } = useTranslation();
  const { className: toneClass, Icon } = TONE[tone];
  const text = label ?? t(`status.${tone}`);
  const classes = cn(
    "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold whitespace-nowrap",
    toneClass,
    className,
  );
  if (onClick) {
    return (
      <button
        type="button"
        data-slot="status-chip"
        data-tone={tone}
        onClick={onClick}
        className={cn(classes, "min-h-9 underline-offset-2 hover:underline")}
        title={tone === "problem" ? t("errors.problem_how_to_fix") : undefined}
      >
        <Icon className="size-3.5" aria-hidden />
        {text}
        {tone === "problem" ? (
          <span className="sr-only"> — {t("errors.problem_how_to_fix")}</span>
        ) : null}
      </button>
    );
  }
  return (
    <span data-slot="status-chip" data-tone={tone} className={classes}>
      <Icon className="size-3.5" aria-hidden />
      {text}
    </span>
  );
}
